import React, { useEffect, useState } from "react";
import {
  useImageUploader,
  useDirectContextUpload,
} from "@/components/AppHeader/FileUpload";
import {
  Loader2,
  RefreshCcw,
  ArrowUp,
  Ban,
  X,
  PaperclipIcon,
  FileIcon,
  CloudUpload,
} from "lucide-react";
import { useSuggestions } from "@/components/ProjectList/hooks/useSuggestions";
import { ProjectCreateLayout } from "@/components/ProjectCreate/layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { onProjectsSnapshot } from "@/store/supabaseSlice";
import { useProjectCreateStore } from "@/store/zustand/projectCreateStore";
import { track } from "@/utils/events/tracker";
import {
  getTemplateForInputs,
  ProjectRunMode,
  ReactFramework,
  TemplateStack,
  DesignSystem,
  CustomDesignSystems,
} from "@/utils/templateProjects";
import type { UiLibrary, TemplateConfig } from "@/utils/templateProjects";
import {
  getAxiosErorrMessage,
  getUnexpiredToken,
  logAxiosError,
  useUserId,
} from "@/utils/utils";
import axios from "axios";
import { useFeatureFlagEnabled, useActiveFeatureFlags } from "posthog-js/react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router";
import { Tooltip } from "react-tooltip";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import ProviderSetup from "@/components/provider-setup";
import type { FormValues } from "@/components/provider-setup";
import useSupabaseConnectStore from "@/store/zustand/supabaseConnectStore";
import { ArrowLeft } from "lucide-react";
import { showNewMessage } from "@intercom/messenger-js-sdk";
import { Switch } from "@/components/ui/switch";
import { useSession, useUser } from "@clerk/clerk-react";
import type { UserResource } from "@clerk/types";
import { useGlobalDragState } from "@/hooks/useDragState";
import DragHoverTargets from "@/components/AI/components/DragHoverTargets";

/**
 * Polls the dev server status and uploads files once the server is ready
 */
const uploadPendingFilesWhenReady = async (
  canvasId: string,
  files: File[],
  uploadFilesFn: (
    files: File[],
    options?: {
      forCodebase?: boolean;
      canvasId?: string;
      codebaseDirectory?: string;
    }
  ) => Promise<void>,
  supabaseJWT: string
): Promise<void> => {
  const maxAttempts = 60; // 5 minutes max wait time
  const delayMs = 5000; // 5 seconds between checks

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const response = await axios.get(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/devServer/devServerStatus`,
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        }
      );

      const { isServerRunning, devServerConsoleConnected, containerStatus } =
        response.data;

      // Check if dev server is fully ready
      if (
        containerStatus?.running &&
        isServerRunning &&
        devServerConsoleConnected
      ) {
        // Dev server is ready, upload the files
        await uploadFilesFn(files, {
          forCodebase: true,
          canvasId,
          codebaseDirectory: undefined, // Use default directory logic
        });
        return;
      }

      // Wait before next check
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    } catch (error) {
      console.warn(
        `Dev server status check failed (attempt ${attempt + 1}):`,
        error
      );

      // If it's a 401/403, the canvas might not be ready yet, continue polling
      if (error.response?.status === 401 || error.response?.status === 403) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        continue;
      }

      // For other errors, wait a bit longer before retrying
      await new Promise((resolve) => setTimeout(resolve, delayMs * 2));
    }
  }

  throw new Error("Dev server did not become ready within the timeout period");
};

const AINewProjectPage = () => {
  const navigate = useNavigate();

  const selectedTemplateStack = useProjectCreateStore(
    (state) => state.selectedTemplateStack
  );
  const setSelectedTemplateStack = useProjectCreateStore(
    (state) => state.setSelectedTemplateStack
  );

  const activeFeatureFlags = useActiveFeatureFlags();

  // redux isnt necessary here
  const [selectedDesignSystem, setSelectedDesignSystem] =
    useState<DesignSystem | null>(null);

  const allowPickAlternateFramework = useFeatureFlagEnabled(
    "pick-alternate-framework"
  );

  const dispatch = useDispatch();

  const { user } = useUser();

  const [searchParams, setSearchParams] = useSearchParams();
  const firstTime = searchParams.get("ft");
  const [generateLoading, setGenerateLoading] = useState(false);

  const { suggestions, refreshSuggestions } = useSuggestions();
  const {
    uploadedFiles,
    FilePreviewerComponent,
    FileUploadComponent,
    uploadFiles,
    handleImagePaste,
    handlePastedFilesAsContext,
    handlePastedFilesToCodebase,
    isContextUploadSupported,
    pastedFiles,
    setPastedFiles,
  } = useImageUploader();

  // Custom hook for direct context upload
  const { handleDirectPaste } = useDirectContextUpload(uploadFiles);

  const { session } = useSession();

  const generateText = useProjectCreateStore((state) => state.generateText);
  const setGenerateText = useProjectCreateStore(
    (state) => state.setGenerateText
  );
  const autoTriggerGenerate = useProjectCreateStore(
    (state) => state.autoTriggerGenerate
  );
  const setAutoTriggerGenerate = useProjectCreateStore(
    (state) => state.setAutoTriggerGenerate
  );

  const setGenerateAttachedFiles = useProjectCreateStore(
    (state) => state.setGenerateAttachedFiles
  );

  const pendingCodebaseFiles = useProjectCreateStore(
    (state) => state.pendingCodebaseFiles
  );
  const addPendingCodebaseFiles = useProjectCreateStore(
    (state) => state.addPendingCodebaseFiles
  );
  const clearPendingCodebaseFiles = useProjectCreateStore(
    (state) => state.clearPendingCodebaseFiles
  );
  const setPendingCodebaseFiles = useProjectCreateStore(
    (state) => state.setPendingCodebaseFiles
  );

  const reactFramework = useProjectCreateStore((state) => state.reactFramework);
  const setReactFramework = useProjectCreateStore(
    (state) => state.setReactFramework
  );

  const supabaseConnectedProjectId = useSupabaseConnectStore(
    (state) => state.connectedProjectId
  );

  const [showIntegrationSetup, setShowIntegrationSetup] = useState(false);
  const [desiredTemplateStack, setDesiredTemplateStack] =
    useState<TemplateStack | null>(null);

  const [envVariables, setEnvVariables] = useState<FormValues>({
    clerk: {
      publishableKey: "",
      secretKey: "",
    },
    convex: {
      deploymentUrl: "",
      deploymentKey: "",
    },
    polar: {
      accessToken: "",
      organizationId: "",
      webhookSecret: "",
      verifyWebhookFields: "",
    },
    stripe: {
      secretKey: "",
      webhookSecret: "",
      publicKey: "",
      verifyWebhookFields: "",
    },
  });

  const [useReactRouterV7, setUseReactRouterV7] = useState(false);
  const [useNextTurbopack, setUseNextTurbopack] = useState(false);

  const { isDragging, resetDragState } = useGlobalDragState();

  useEffect(() => {
    // Set defaults
    setReactFramework(ReactFramework.VITE);
    setSelectedTemplateStack(TemplateStack.BLANK);
    setSelectedDesignSystem(DesignSystem.BLANK);
    // Clear any pending files from previous sessions
    clearPendingCodebaseFiles();
  }, [setReactFramework, setSelectedTemplateStack, clearPendingCodebaseFiles]);

  const showDesignSystem = useFeatureFlagEnabled("custom_design_system");

  const [generating, setGenerating] = React.useState(false);

  const userId = useUserId();

  const handleStackSelection = (stack: TemplateStack) => {
    // Special case for BLANK - set to null to represent "None" for both Auth/DB and Payments
    if (stack === TemplateStack.BLANK) {
      setSelectedTemplateStack(TemplateStack.BLANK);
      return;
    }

    if (stack === TemplateStack.CLERK_CONVEX) {
      setReactFramework(ReactFramework.VITE);
    } else if (stack === TemplateStack.CLERK_CONVEX_STRIPE) {
      setReactFramework(ReactFramework.VITE);
    } else if (
      stack === TemplateStack.SUPABASE_POLAR ||
      stack === TemplateStack.SUPABASE_STRIPE
    ) {
      setReactFramework(ReactFramework.NEXT_JS);
    } else if (reactFramework === ReactFramework.EXPO) {
      setReactFramework(ReactFramework.VITE);
    }

    setDesiredTemplateStack(stack);
    setShowIntegrationSetup(true);
  };

  const onFrameworkChange = (
    framework:
      | ReactFramework.VITE
      | ReactFramework.NEXT_JS
      | ReactFramework.EXPO
  ) => {
    // No change
    if (framework === reactFramework) {
      return;
    }

    if (framework !== ReactFramework.VITE) {
      setUseReactRouterV7(false);
    }

    if (framework !== ReactFramework.NEXT_JS) {
      setUseNextTurbopack(false);
    }

    if (framework === ReactFramework.EXPO) {
      // Just change and ignore all others (they will be disabled)
      setReactFramework(ReactFramework.EXPO);

      return;
    }

    if (
      selectedTemplateStack === TemplateStack.CLERK_CONVEX_STRIPE &&
      framework === ReactFramework.NEXT_JS
    ) {
      // Silently prevent the change without showing an error
      return;
    }

    // Reset Clerk+Convex selections when switching to Next.js
    if (framework === ReactFramework.NEXT_JS) {
      if (
        selectedTemplateStack === TemplateStack.CLERK_CONVEX ||
        selectedTemplateStack === TemplateStack.CLERK_CONVEX_POLAR ||
        selectedTemplateStack === TemplateStack.CLERK_CONVEX_STRIPE
      ) {
        // Silently reset to blank without showing an error
        setSelectedTemplateStack(TemplateStack.BLANK);
      }
    }

    setReactFramework(framework);
  };

  const getEnvironmentVariables = (
    reactFramework: ReactFramework,
    selectedTemplateStack: TemplateStack,
    envVariables: FormValues
  ) => {
    // For non-Supabase stacks, include all relevant environment variables
    if (reactFramework === ReactFramework.NEXT_JS) {
      // For Supabase stacks, only include payment-related environment variables
      // Supabase connection variables are handled by the connection process
      if (selectedTemplateStack === TemplateStack.SUPABASE_POLAR) {
        return {
          POLAR_ORGANIZATION_ID: envVariables?.polar.organizationId,
          POLAR_ACCESS_TOKEN: envVariables?.polar.accessToken,
          POLAR_WEBHOOK_SECRET: envVariables?.polar.webhookSecret,
        };
      }

      if (selectedTemplateStack === TemplateStack.SUPABASE_STRIPE) {
        return {
          STRIPE_SECRET_KEY: envVariables?.stripe?.secretKey,
          STRIPE_WEBHOOK_SECRET: envVariables?.stripe?.webhookSecret,
        };
      }
    }

    if (reactFramework === ReactFramework.VITE) {
      if (selectedTemplateStack === TemplateStack.CLERK_CONVEX) {
        return {
          VITE_CLERK_PUBLISHABLE_KEY: envVariables?.clerk?.publishableKey,
          CONVEX_DEPLOYMENT:
            envVariables?.convex?.deploymentKey?.split("|")?.[0],
          VITE_CONVEX_URL: envVariables?.convex?.deploymentUrl,
        };
      }

      if (selectedTemplateStack === TemplateStack.CLERK_CONVEX_POLAR) {
        // For Vite with Polar, don't include Polar keys as they'll be stored in Convex
        return {
          VITE_CLERK_PUBLISHABLE_KEY: envVariables?.clerk?.publishableKey,
          CONVEX_DEPLOYMENT:
            envVariables?.convex?.deploymentKey?.split("|")?.[0],
          VITE_CONVEX_URL: envVariables?.convex?.deploymentUrl,
        };
      }

      if (selectedTemplateStack === TemplateStack.CLERK_CONVEX_STRIPE) {
        // For Vite with Stripe, don't include Stripe keys as they'll be stored in Convex
        return {
          VITE_CLERK_PUBLISHABLE_KEY: envVariables?.clerk?.publishableKey,
          CONVEX_DEPLOYMENT:
            envVariables?.convex?.deploymentKey?.split("|")?.[0],
          VITE_CONVEX_URL: envVariables?.convex?.deploymentUrl,
        };
      }

      // Handle Vite with Supabase and payment providers
      if (selectedTemplateStack === TemplateStack.SUPABASE_STRIPE) {
        return {
          STRIPE_SECRET_KEY: envVariables?.stripe?.secretKey,
          STRIPE_WEBHOOK_SECRET: envVariables?.stripe?.webhookSecret,
        };
      }

      if (selectedTemplateStack === TemplateStack.SUPABASE_POLAR) {
        return {
          POLAR_ORGANIZATION_ID: envVariables?.polar.organizationId,
          POLAR_ACCESS_TOKEN: envVariables?.polar.accessToken,
          POLAR_WEBHOOK_SECRET: envVariables?.polar.webhookSecret,
        };
      }
    }

    return {};
  };

  const createProject = async (
    template: TemplateConfig,
    user: UserResource | null | undefined,
    supabaseJWT: string,
    envVariables?: Record<string, string>,
    designSystem?: DesignSystem | null
  ) => {
    if (!userId) {
      throw new Error("User not found");
    }

    const response = await axios.post(
      `${import.meta.env.VITE_BACKEND_BASE_URL}/projects/add`,
      {
        repoName: template.projectName,
        repoPath: template.repoPath,
        framework: template.framework,
        nodeVersion: template.nodeVersion,
        packageManager: template.packageManager,
        userId: userId,
        runMode: ProjectRunMode.CLOUD,
        environmentVariables: envVariables || {},
        designSystem: designSystem,
      },
      {
        headers: {
          Authorization: `Bearer ${supabaseJWT}`,
        },
      }
    );

    return response.data;
  };

  const generate = async () => {
    if (generating) return;

    try {
      const supabaseJWT = await getUnexpiredToken(session);

      if (!supabaseJWT) {
        return;
      }

      setGenerating(true);

      const template = getTemplateForInputs(
        reactFramework,
        selectedTemplateStack,
        useReactRouterV7,
        useNextTurbopack
      );

      if (!template) {
        toast.error("Could not find a suitable template");
        return;
      }

      // Format environment variables for the selected stack
      // For Supabase stacks, we don't need to include Supabase env vars as they'll be handled by the connection
      const rawEnvVars = getEnvironmentVariables(
        reactFramework,
        selectedTemplateStack,
        envVariables
      );

      // Filter out undefined values
      const formattedEnvVars = Object.fromEntries(
        Object.entries(rawEnvVars).filter(([_, value]) => value !== undefined)
      ) as Record<string, string>;

      const { project: createdProject, canvas: createCanvas } =
        await createProject(
          template,
          user,
          supabaseJWT,
          formattedEnvVars,
          selectedDesignSystem
        );

      track("ADD_PROJECT", { step: "GENERATE", canvas_id: createCanvas.id });
      track("AI_PROMPT_REQUESTED", {
        type: "AI_NEW_PROJECT",
        prompt: generateText,
        imageUrls: uploadedFiles.map((file) => file.url),
        canvas_id: createCanvas.id,
      });

      track("TRACK_USED_RUNNING_TEMPLATE", {
        used: Boolean(createdProject?.templateUserId),
        repoPath: createdProject?.repoPath,
      });

      // Update store
      dispatch(
        onProjectsSnapshot({
          newItems: [createdProject],
          deletedItems: [],
        })
      );

      if (
        selectedTemplateStack?.includes("SUPABASE") &&
        supabaseConnectedProjectId
      ) {
        try {
          // Make the API call after the project is created now that we can
          await axios.post(
            `${
              import.meta.env.VITE_BACKEND_BASE_URL
            }/projects/supabase-connect/${createdProject.id}/connect-project`,
            {
              supabaseProjectId: supabaseConnectedProjectId,
              isFtue: true,

              // Also set the env variables in supabase
              envVariablesForSupabase: formattedEnvVars,
            },
            {
              headers: {
                Authorization: `Bearer ${supabaseJWT}`,
              },
            }
          );
        } catch (e) {
          logAxiosError("Error connecting supabase project", e);
          toast.error(
            `Error connecting Supabase project, continuing without it: ${getAxiosErorrMessage(
              e
            )}`
          );
        }
      }

      // Upload any pending codebase files after canvas creation and dev server is ready
      if (pendingCodebaseFiles.length > 0) {
        // Don't await this - let it run in the background while user navigates
        uploadPendingFilesWhenReady(
          createCanvas.id,
          pendingCodebaseFiles,
          uploadFiles,
          supabaseJWT
        )
          .then(() => {
            clearPendingCodebaseFiles();
            toast.success(
              `Uploaded ${pendingCodebaseFiles.length} file(s) to codebase`
            );
          })
          .catch((error) => {
            console.error("Error uploading pending codebase files:", error);
            toast.error("Some files could not be uploaded to codebase");
          });
      }

      navigate(`/canvases/${createCanvas.id}/editor?generate-prd=t`);
    } catch (error) {
      logAxiosError("Error creating new project", error);
      toast.error(
        `Could not create a new project: ${getAxiosErorrMessage(error)}`
      );
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (generateText && autoTriggerGenerate) {
      setAutoTriggerGenerate(false);
      generate();
    }
  }, [generateText, autoTriggerGenerate, setAutoTriggerGenerate, generate]);

  useEffect(() => {
    if (uploadedFiles.length > 0) {
      setGenerateAttachedFiles(
        uploadedFiles.map((file) => ({
          url: file.url,
          name: file.name,
          type: file.type,
          mimeType: file.mimeType,
        }))
      );
    } else {
      setGenerateAttachedFiles(null);
    }
  }, [uploadedFiles, setGenerateAttachedFiles]);

  if (showIntegrationSetup) {
    return (
      <ProviderSetup
        // @ts-expect-error TS2322
        desiredTemplateStack={desiredTemplateStack}
        reactFramework={reactFramework}
        setReactFramework={setReactFramework}
        onBack={() => {
          setDesiredTemplateStack(null);
          setShowIntegrationSetup(false);
        }}
        onComplete={(completedTemplateStack) => {
          setDesiredTemplateStack(null);
          setShowIntegrationSetup(false);
          setSelectedTemplateStack(completedTemplateStack);
        }}
        formValues={envVariables}
        onFormValuesChange={(formValues) => {
          setEnvVariables(formValues);
        }}
      />
    );
  }

  return (
    <ProjectCreateLayout showProjectName={true} hideCreateProject={true}>
      <div className="flex flex-col flex-1 justify-center items-center w-full text-md overflow-auto relative max-w-screen-lg mx-8 gap-3">
        <Button
          variant="outline"
          size="sm"
          style={{
            borderWidth: "1px",
            borderColor: "rgba(47, 48, 53, 1)",
            borderStyle: "solid",
          }}
          className="flex items-center rounded-lg gap-2 self-start"
          onClick={() => navigate("/project-create/workflow-select")}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <div className="font-[500] text-[36px] font-brockmann leading-[57.6px]">
          What do you want to build?
        </div>
        <div
          className="bg-secondary-n-950 max-w-[600px] w-full rounded-lg p-1.5 pl-3 flex flex-col text-xs gap-2"
          onPaste={handleDirectPaste}
        >
          <div className="flex">
            {uploadedFiles.length !== 0 && (
              <div
                className={`${
                  uploadedFiles.length > 0 ? "min-h-[10px]" : "min-h-[20px]"
                }`}
              >
                <FilePreviewerComponent />
              </div>
            )}
            {pendingCodebaseFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 my-2">
                {pendingCodebaseFiles.map((file, index) => (
                  <div
                    key={file.name + file.size + file.lastModified}
                    className="relative w-fit rounded-md"
                  >
                    <X
                      className="absolute cursor-pointer -top-1 -right-1.5 z-10 w-5 h-5 hover:text-icons-primary text-icons-secondary bg-background-secondary hover:bg-background-accent rounded-md p-0.5 size-5"
                      onClick={() => {
                        const newFiles = pendingCodebaseFiles.filter(
                          (_, i) => i !== index
                        );
                        setPendingCodebaseFiles(newFiles);
                      }}
                    />
                    <div
                      className={cn(
                        "h-16 w-16 rounded-md relative flex items-center justify-center p-1",
                        file.type.startsWith("image/")
                          ? "checkerboard"
                          : "bg-background-secondary"
                      )}
                      style={
                        file.type.startsWith("image/")
                          ? {
                              backgroundImage: `
                        linear-gradient(45deg, var(--background-secondary) 25%, transparent 25%), 
                        linear-gradient(-45deg, var(--background-secondary) 25%, transparent 25%), 
                        linear-gradient(45deg, transparent 75%, var(--background-secondary) 75%), 
                        linear-gradient(-45deg, transparent 75%, var(--background-secondary) 75%)
                      `,
                              backgroundSize: "8px 8px",
                              backgroundPosition:
                                "0 0, 0 4px, 4px -4px, -4px 0px",
                              backgroundColor: "bg-background-secondary",
                            }
                          : undefined
                      }
                    >
                      {file.type.startsWith("image/") ? (
                        <img
                          src={URL.createObjectURL(file)}
                          alt="Pending upload"
                          height={100}
                          width={100}
                          className="h-10 w-16 rounded-sm relative flex items-center justify-center object-contain"
                          onLoad={(e) => {
                            // Clean up the object URL after the image loads
                            setTimeout(
                              () => URL.revokeObjectURL(e.currentTarget.src),
                              1000
                            );
                          }}
                        />
                      ) : file.type === "application/pdf" ? (
                        <div className="flex flex-col items-center justify-center w-full h-full">
                          <FileIcon className="size-4 text-icons-primary" />
                          <span className="text-xs truncate max-w-full">
                            PDF
                          </span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center w-full h-full">
                          <FileIcon className="size-4 text-icons-primary" />
                          <span className="text-xs truncate max-w-full">
                            File
                          </span>
                        </div>
                      )}
                      <CloudUpload className="text-icons-secondary absolute bottom-1 left-1 z-10 size-4" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div
            className={`w-full flex flex-col mt-[0.5rem] ${
              uploadedFiles.length === 0 ? "min-h-[90px]" : "min-h-[70px]"
            } justify-between items-end`}
          >
            {isDragging ? (
              <div className="w-full relative">
                <DragHoverTargets
                  pastedFiles={[]} // No longer handle pasted files here since they upload immediately
                  isContextDisabled={false}
                  onUploadFiles={(files) => {
                    uploadFiles(files);
                    resetDragState();
                  }}
                  onUploadToCodebase={(files) => {
                    // Store dropped files for later upload since we don't have a canvas ID yet
                    addPendingCodebaseFiles(files);
                    resetDragState();
                    toast.success(
                      `${files.length} file(s) will be uploaded to codebase after project creation`
                    );
                  }}
                />
              </div>
            ) : (
              <>
                <Textarea
                  className={`bg-transparent border-0 ${
                    uploadedFiles.length !== 0 ? "py-0" : "py-2"
                  } h-full outline-0
                  focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 
                  focus-visible:ring-offset-0 focus:border-0 focus-visible:border-0 w-full 
                  text-[#A6A7AB]
                  placeholder:text-[16px] placeholder-[#5C5F66] resize-none text-[16px] shadow-none
                  leading-5`}
                  placeholder="Onboarding page for ed tech app in minimal UI"
                  value={generateText || ""}
                  rows={1}
                  onChange={(e) => setGenerateText(e.target.value)}
                  onKeyDown={(e) => {
                    if (generating) {
                      return;
                    }

                    if (e.key === "Enter" && !e.shiftKey) {
                      generate();
                      e.stopPropagation();
                      e.preventDefault();
                    }
                  }}
                />
                <div className="flex flex-row gap-2">
                  <FileUploadComponent
                    className="max-w-min-content"
                    buttonLabel="Upload"
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    className="h-7 text-xs gap-1 px-3 rounded-md"
                    onClick={() => {
                      if (generating) {
                        return;
                      }

                      generate();
                    }}
                    disabled={generating}
                  >
                    <div
                      className={cn(
                        "animate-spin mr-1",
                        !generating && "hidden"
                      )}
                    >
                      <Loader2 className="h-4 w-4" />
                    </div>
                    <span>Next</span>
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 w-full items-center justify-center pb-2">
          {Object.keys(suggestions).map((suggestion, index) => {
            return (
              <button
                type="button"
                className="flex flex-row gap-3 items-center justify-between border w-fit text-xs 
                p-[10px] border-[#1F1F22] transition-all duration-500 ease-in-out 
                rounded-[8px] text-secondary-900 cursor-pointer hover:bg-secondary-300 bg-transparent"
                key={suggestion}
                onClick={() => setGenerateText(suggestions[suggestion])}
                aria-label={`Use suggestion: ${suggestions[suggestion]}`}
              >
                {suggestions[suggestion]}
                <ArrowUp className="w-3 h-3" />
              </button>
            );
          })}
          <button
            type="button"
            className="bg-secondary-200 text-secondary-800 hover:bg-secondary-300 cursor-pointer rounded-md p-1"
            onClick={refreshSuggestions}
            aria-label="Refresh suggestions"
          >
            <RefreshCcw className="w-3 h-3" />
          </button>
        </div>
        <div className="flex flex-col justify-center items-center gap-8 mt-[2rem]">
          <div className="flex flex-row gap-4">
            <div className="flex flex-col justify-center items-center">
              <h1 className="text-gray-400 mb-4">Framework:</h1>
              <div className="flex flex-row bg-gradient-to-b from-[#1F1F22] to-[#131519] p-3 w-fit rounded-lg gap-4">
                <img
                  src={"/images/vitejs.svg"}
                  alt="Vite"
                  className={`w-6 h-6 cursor-pointer transition-opacity duration-200 ${
                    reactFramework === ReactFramework.VITE
                      ? "opacity-100"
                      : "opacity-10 hover:opacity-70"
                  }`}
                  onClick={() => onFrameworkChange(ReactFramework.VITE)}
                  data-tooltip-id="tooltip"
                  data-tooltip-content="Vite"
                />

                <img
                  src={"/images/nextjs_icon_dark.svg"}
                  alt="Next.js"
                  className={`w-6 h-6 cursor-pointer transition-opacity duration-200 ${
                    reactFramework === ReactFramework.NEXT_JS
                      ? "opacity-100"
                      : "opacity-10 hover:opacity-70"
                  }`}
                  onClick={() => onFrameworkChange(ReactFramework.NEXT_JS)}
                  data-tooltip-id="tooltip"
                  data-tooltip-content="Next.js"
                />

                <img
                  src={"/images/expo.svg"}
                  alt="Expo"
                  className={`w-6 h-6 cursor-pointer transition-opacity duration-200 ${
                    reactFramework === ReactFramework.EXPO
                      ? "opacity-100"
                      : "opacity-10 hover:opacity-70"
                  }`}
                  onClick={() => onFrameworkChange(ReactFramework.EXPO)}
                  data-tooltip-id="tooltip"
                  data-tooltip-content="Expo"
                />
              </div>
            </div>
            {showDesignSystem && (
              <div className="flex flex-col justify-center items-center">
                <h1 className="text-gray-400 mb-4">Design System:</h1>
                <div className="flex flex-row gap-3">
                  <div
                    className={cn(
                      "flex flex-row bg-gradient-to-b from-[#1F1F22] to-[#131519] p-3 w-fit rounded-lg gap-4 cursor-pointer group"
                    )}
                    onClick={() => setSelectedDesignSystem(DesignSystem.BLANK)}
                    data-tooltip-id="tooltip"
                    data-tooltip-content="No Design System"
                  >
                    <Ban
                      className={cn(
                        "w-6 h-6 transition-opacity duration-200",
                        selectedDesignSystem !== DesignSystem.BLANK &&
                          "opacity-30 group-hover:opacity-70"
                      )}
                    />
                  </div>
                  {CustomDesignSystems.map((designSystem) => {
                    if (
                      !activeFeatureFlags?.includes(designSystem.featureFlag)
                    ) {
                      return null;
                    }

                    let disabled = false;
                    if (reactFramework !== designSystem.framework) {
                      disabled = true;
                    }

                    if (designSystem.importName === DesignSystem.GUIDEWIRE) {
                      if (
                        reactFramework === ReactFramework.NEXT_JS_APP_ROUTER
                      ) {
                        disabled = false;
                      }
                    }

                    return (
                      <div
                        className={cn(
                          "flex flex-row bg-gradient-to-b from-[#1F1F22] to-[#131519] p-3 w-fit rounded-lg gap-4 cursor-pointer group",
                          disabled && "cursor-default"
                        )}
                        onClick={() => {
                          if (disabled) return;

                          if (
                            designSystem.importName === DesignSystem.GUIDEWIRE
                          ) {
                            setReactFramework(
                              ReactFramework.NEXT_JS_APP_ROUTER
                            );
                          }

                          setSelectedDesignSystem(designSystem.importName);
                          setSelectedTemplateStack(designSystem.templateStack);
                        }}
                        data-tooltip-id="tooltip"
                        data-tooltip-content={designSystem.title}
                      >
                        <img
                          src={designSystem.icon}
                          alt={designSystem.title}
                          className={cn(
                            "w-6 h-6 transition-opacity duration-200",
                            disabled
                              ? "opacity-30 group-hover:opacity-30"
                              : "opacity-30 group-hover:opacity-70",
                            selectedDesignSystem === designSystem.importName &&
                              "opacity-100"
                          )}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-row gap-12 items-center justify-center w-full">
            <div className="flex flex-col justify-center items-center">
              <h1 className="text-gray-400 mb-4">Auth/DB:</h1>
              <div className="flex flex-row gap-3">
                {(() => {
                  let selected = false;
                  if (
                    selectedTemplateStack === TemplateStack.BLANK ||
                    selectedTemplateStack === TemplateStack.SLDS ||
                    selectedTemplateStack === null ||
                    reactFramework === ReactFramework.EXPO
                  ) {
                    selected = true;
                  }

                  return (
                    <>
                      <div
                        className={cn(
                          "flex flex-row bg-gradient-to-b from-[#1F1F22] to-[#131519] p-3 w-fit rounded-lg gap-4 cursor-pointer"
                        )}
                        onClick={() => {
                          if (!selected) {
                            handleStackSelection(TemplateStack.BLANK);
                          }
                        }}
                        data-tooltip-id="tooltip"
                        data-tooltip-content="Blank Template"
                      >
                        <Ban
                          className={cn(
                            "w-6 h-6 transition-opacity duration-200",
                            !selected && "opacity-30 group-hover:opacity-70"
                          )}
                        />
                      </div>
                    </>
                  );
                })()}
                {(() => {
                  let disabled = false,
                    selected = false;
                  if (
                    reactFramework === ReactFramework.EXPO ||
                    selectedTemplateStack === TemplateStack.SLDS ||
                    selectedTemplateStack === TemplateStack.QUILL
                  ) {
                    disabled = true;
                  }
                  if (
                    selectedTemplateStack === TemplateStack.SUPABASE ||
                    selectedTemplateStack === TemplateStack.SUPABASE_POLAR ||
                    selectedTemplateStack === TemplateStack.SUPABASE_STRIPE
                  ) {
                    selected = true;
                  }

                  return (
                    <>
                      <div
                        className={cn(
                          "flex flex-row justify-center items-center bg-gradient-to-b from-[#1F1F22] to-[#131519] p-3 w-fit rounded-lg gap-4 cursor-pointer group",
                          disabled && "cursor-default"
                        )}
                        onClick={() => {
                          if (!disabled && !selected) {
                            handleStackSelection(TemplateStack.SUPABASE);
                          }
                        }}
                        data-tooltip-id="tooltip"
                        data-tooltip-content="Supabase"
                      >
                        <img
                          src={"/images/supabase-logo-icon.svg"}
                          alt="Supabase"
                          className={cn(
                            "w-6 h-6 transition-opacity duration-200",
                            !selected && "opacity-30 group-hover:opacity-70",
                            disabled && "opacity-10 group-hover:opacity-10"
                          )}
                        />
                      </div>
                    </>
                  );
                })()}
                {(() => {
                  let disabled = false,
                    selected = false;
                  if (
                    reactFramework === ReactFramework.NEXT_JS ||
                    reactFramework === ReactFramework.EXPO ||
                    selectedTemplateStack === TemplateStack.SLDS ||
                    selectedTemplateStack === TemplateStack.QUILL
                  ) {
                    disabled = true;
                  }
                  if (
                    selectedTemplateStack === TemplateStack.CLERK_CONVEX ||
                    selectedTemplateStack ===
                      TemplateStack.CLERK_CONVEX_POLAR ||
                    selectedTemplateStack === TemplateStack.CLERK_CONVEX_STRIPE
                  ) {
                    selected = true;
                  }

                  return (
                    <>
                      <div
                        className={cn(
                          `flex flex-row justify-center items-center bg-gradient-to-b from-[#1F1F22] to-[#131519] p-3 w-fit rounded-lg gap-4 group cursor-pointer`,
                          disabled && "cursor-default"
                        )}
                        onClick={() => {
                          if (!disabled && !selected) {
                            handleStackSelection(TemplateStack.CLERK_CONVEX);
                          }
                          // No error toast if Next.js is selected
                        }}
                        data-tooltip-id="tooltip"
                        data-tooltip-content={`${
                          reactFramework !== ReactFramework.VITE
                            ? "Clerk & Convex (Vite only)"
                            : "Clerk & Convex"
                        }`}
                      >
                        <img
                          src={"/images/clerk-auth.png"}
                          alt="Clerk"
                          className={cn(
                            "w-6 h-6 transition-opacity duration-200",
                            !selected && "opacity-30 group-hover:opacity-70",
                            disabled && "opacity-10 group-hover:opacity-10"
                          )}
                        />
                        <img
                          src={"/images/convex.png"}
                          alt="Convex"
                          className={cn(
                            "w-6 h-6 transition-opacity duration-200",
                            !selected && "opacity-30 group-hover:opacity-70",
                            disabled && "opacity-10 group-hover:opacity-10"
                          )}
                        />
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
            <div className="flex flex-col justify-center items-center">
              <h1 className="text-gray-400 mb-4">Payments:</h1>
              <div className="flex flex-row gap-3">
                {(() => {
                  let selected = false;
                  if (
                    selectedTemplateStack === TemplateStack.BLANK ||
                    selectedTemplateStack === null ||
                    selectedTemplateStack === TemplateStack.SUPABASE ||
                    selectedTemplateStack === TemplateStack.CLERK_CONVEX
                  ) {
                    selected = true;
                  }

                  return (
                    <div
                      className={cn(
                        `flex flex-row bg-gradient-to-b from-[#1F1F22] to-[#131519] p-3 w-fit rounded-lg gap-4 cursor-pointer group`
                      )}
                      onClick={() => handleStackSelection(TemplateStack.BLANK)}
                      data-tooltip-id="tooltip"
                      data-tooltip-content="No Payment"
                    >
                      <Ban
                        className={cn(
                          "w-6 h-6 transition-opacity duration-200",
                          !selected && "opacity-30 group-hover:opacity-70"
                        )}
                      />
                    </div>
                  );
                })()}

                {(() => {
                  let disabled = false,
                    selected = false;
                  if (
                    reactFramework === ReactFramework.EXPO ||
                    selectedTemplateStack === TemplateStack.SLDS ||
                    selectedTemplateStack === TemplateStack.QUILL
                  ) {
                    disabled = true;
                  }
                  if (
                    selectedTemplateStack === TemplateStack.SUPABASE_STRIPE ||
                    selectedTemplateStack === TemplateStack.CLERK_CONVEX_STRIPE
                  ) {
                    selected = true;
                  }

                  return (
                    <div
                      className={cn(
                        "flex flex-row justify-center items-center bg-gradient-to-b from-[#1F1F22] to-[#131519] p-3 w-fit rounded-lg gap-4 cursor-pointer group",
                        disabled && "cursor-default"
                      )}
                      onClick={() => {
                        if (disabled || selected) {
                          return;
                        }

                        // If Next.js is selected, always use Supabase with Stripe
                        if (reactFramework === ReactFramework.NEXT_JS) {
                          handleStackSelection(TemplateStack.SUPABASE_STRIPE);
                        } else if (
                          selectedTemplateStack === TemplateStack.SUPABASE
                        ) {
                          // Keep Supabase if it's already selected
                          handleStackSelection(TemplateStack.SUPABASE_STRIPE);
                        } else if (
                          selectedTemplateStack === TemplateStack.CLERK_CONVEX
                        ) {
                          // Keep Clerk+Convex if it's already selected
                          handleStackSelection(
                            TemplateStack.CLERK_CONVEX_STRIPE
                          );
                        } else {
                          // Default based on framework
                          handleStackSelection(
                            TemplateStack.CLERK_CONVEX_STRIPE
                          );
                        }
                      }}
                      data-tooltip-id="tooltip"
                      data-tooltip-content="Stripe"
                    >
                      <img
                        src={"/images/stripe.svg"}
                        alt="Stripe"
                        className={cn(
                          "w-6 h-6 transition-opacity duration-200",
                          !selected && "opacity-30 group-hover:opacity-70",
                          disabled && "opacity-10 group-hover:opacity-10"
                        )}
                      />
                    </div>
                  );
                })()}

                {(() => {
                  let disabled = false,
                    selected = false;
                  if (
                    reactFramework === ReactFramework.EXPO ||
                    selectedTemplateStack === TemplateStack.SLDS ||
                    selectedTemplateStack === TemplateStack.QUILL
                  ) {
                    disabled = true;
                  }
                  if (
                    selectedTemplateStack === TemplateStack.SUPABASE_POLAR ||
                    selectedTemplateStack === TemplateStack.CLERK_CONVEX_POLAR
                  ) {
                    selected = true;
                  }

                  return (
                    <div
                      className={cn(
                        "flex flex-row justify-center items-center bg-gradient-to-b from-[#1F1F22] to-[#131519] p-3 w-fit rounded-lg gap-4 cursor-pointer group",
                        disabled && "cursor-default"
                      )}
                      onClick={() => {
                        if (disabled || selected) {
                          return;
                        }

                        // If Next.js is selected, always use Supabase with Polar
                        if (reactFramework === ReactFramework.NEXT_JS) {
                          handleStackSelection(TemplateStack.SUPABASE_POLAR);
                        } else if (
                          selectedTemplateStack === TemplateStack.SUPABASE
                        ) {
                          // Keep Supabase if it's already selected
                          handleStackSelection(TemplateStack.SUPABASE_POLAR);
                        } else if (
                          selectedTemplateStack === TemplateStack.CLERK_CONVEX
                        ) {
                          // Keep Clerk+Convex if it's already selected
                          handleStackSelection(
                            TemplateStack.CLERK_CONVEX_POLAR
                          );
                        } else {
                          // Default based on framework
                          handleStackSelection(
                            TemplateStack.CLERK_CONVEX_POLAR
                          );
                        }
                      }}
                      data-tooltip-id="tooltip"
                      data-tooltip-content="Polar"
                    >
                      <img
                        src={"/images/polar-sh_dark.svg"}
                        alt="Polar"
                        className={cn(
                          "w-6 h-6 transition-opacity duration-200",
                          !selected && "opacity-30 group-hover:opacity-70",
                          disabled && "opacity-10 group-hover:opacity-10"
                        )}
                      />
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
          {(reactFramework === ReactFramework.VITE ||
            reactFramework === ReactFramework.NEXT_JS) &&
            allowPickAlternateFramework && (
              <div className="flex flex-col items-center gap-4 mt-8 w-full max-w-md">
                <div className="w-full">
                  <h1 className="text-gray-400 mb-4 text-center">
                    Advanced Options:
                  </h1>
                  <div className="w-full bg-gradient-to-b from-[#1F1F22] to-[#131519] p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <label className="text-sm font-medium text-gray-300">
                            {reactFramework === ReactFramework.VITE
                              ? "React Router 7"
                              : "Next.js Turbopack"}
                          </label>
                          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-yellow-900/30 text-yellow-500 rounded">
                            Beta
                          </span>
                        </div>
                        <p className="text-[12px] text-gray-400">
                          {reactFramework === ReactFramework.VITE
                            ? "Use the latest version of React Router with file-based routing"
                            : "Use the latest version of Next.js with Turbopack"}
                        </p>
                      </div>
                      <Switch
                        checked={
                          reactFramework === ReactFramework.VITE
                            ? useReactRouterV7
                            : useNextTurbopack
                        }
                        onCheckedChange={
                          reactFramework === ReactFramework.VITE
                            ? setUseReactRouterV7
                            : setUseNextTurbopack
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
        </div>
      </div>
    </ProjectCreateLayout>
  );
};

export default AINewProjectPage;
